import React, { useState } from 'react';

// Pass download security (2026-08-30) — rendered instead of the normal
// pass card/CTA stack when walletHandler's fetchPassData() returns
// {challenge_required: true}: this pass has been visited from more
// distinct devices than the merchant's device_lock_enabled threshold
// allows. Mirrors JoinProgramLanding.tsx's mobile+OTP form shape, hitting
// walletDownloadGuard's send-code/verify instead of joinProgramResolver's.
//
// Both mobile and email are required (capture, not compare-against-prior
// data — the merchant's own audience data for this pass may be sparse or
// absent). On success, the parent re-fetches the normal preview.

const PHONE_PATTERN = /^\+?[0-9 ()-]{8,20}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DownloadChallenge: React.FC<{
  buildApiUrl: (suffix: string) => string;
  keyword: string;
  onVerified: () => void;
}> = ({ buildApiUrl, keyword, onVerified }) => {
  const [step, setStep] = useState<'enter-contact' | 'enter-code'>('enter-contact');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!PHONE_PATTERN.test(mobile.trim())) {
      setFieldError('Enter a valid mobile number.');
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      const res = await fetch(buildApiUrl(`/wallet/download-guard/${keyword}/send-code`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: mobile.trim(), email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFieldError(data?.detail || data?.error || 'Something went wrong — please try again.');
        setBusy(false);
        return;
      }
      setStep('enter-code');
      setBusy(false);
    } catch {
      setFieldError('Something went wrong — please try again.');
      setBusy(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setFieldError('Enter the code we texted you.');
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      const res = await fetch(buildApiUrl(`/wallet/download-guard/${keyword}/verify`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFieldError(data?.detail || data?.error || 'Something went wrong — please try again.');
        setBusy(false);
        return;
      }
      onVerified();
    } catch {
      setFieldError('Something went wrong — please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-5">
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      </div>
      <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">Quick check before you download</h1>
      <p className="text-sm text-gray-600 mb-6 text-center">
        This pass has been downloaded on a number of different devices — enter your details and
        we'll text you a code to confirm it's really you.
      </p>

      {step === 'enter-contact' && (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile number</label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
              placeholder="+61 4XX XXX XXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
              placeholder="you@example.com"
              required
            />
          </div>
          {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] transition disabled:opacity-60"
          >
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      )}

      {step === 'enter-code' && (
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 h-11 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-[#AEDD1F]"
              placeholder="123456"
              autoFocus
            />
          </div>
          {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-full bg-[#AEDD1F] text-[#06251B] text-sm font-semibold hover:bg-[#9CCB1B] transition disabled:opacity-60"
          >
            {busy ? 'Verifying…' : 'Verify & continue'}
          </button>
        </form>
      )}
    </div>
  );
};

export default DownloadChallenge;
